import React, { useState, useEffect, useRef, useCallback } from 'react';
import { shareApi } from '../utils/api';
import { showSuccess, showError } from '../utils/toastUtils';
import ErrorMessage from './ErrorMessage';

const ShareManagement = () => {
    const [shares, setShares] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalShares, setTotalShares] = useState(0);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const scrollContainerRef = useRef(null);
    const observerRef = useRef(null);

    const loadShares = async (pageNum = 1, appendMode = false) => {
        if (appendMode) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError('');

        try {
            const result = await shareApi.getUserShares(pageNum, 12); // Page size: 12 items
            console.log('Share API result:', result); // Debug log
            
            if (appendMode) {
                // Append new shares to existing list
                setShares(prev => [...prev, ...(result.shares || [])]);
            } else {
                // Replace shares
                setShares(result.shares || []);
            }
            
            setTotalPages(result.total_pages || 1);
            setTotalShares(result.total_shares || 0);
            setPage(pageNum);
        } catch (err) {
            console.error('Error loading shares:', err);
            setError('Có lỗi xảy ra khi tải danh sách chia sẻ');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const loadMoreShares = useCallback(() => {
        if (page < totalPages && !loadingMore) {
            loadShares(page + 1, true);
        }
    }, [page, totalPages, loadingMore]);

    useEffect(() => {
        loadShares();
    }, []);

    // Auto-load more if no scrollbar exists but more pages available
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || loading || loadingMore) return;

        const hasScrollbar = container.scrollHeight > container.clientHeight;

        // If no scrollbar but more pages available, load more
        if (!hasScrollbar && page < totalPages) {
            loadMoreShares();
        }
    }, [shares, page, totalPages, loading, loadingMore, loadMoreShares]);

    // Infinite scroll observer
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

            // Load more when scrolled to 80% of the content
            if (scrollPercentage > 0.8 && page < totalPages && !loadingMore && !loading) {
                loadMoreShares();
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [page, totalPages, loadingMore, loading, loadMoreShares]);


    const handleDelete = async (shareId) => {
        try {
            await shareApi.deleteShare(shareId);
            
            // Xóa khỏi state
            setShares(prev => prev.filter(share => share.share_id !== shareId));
            
            // Cập nhật total counts
            setTotalShares(prev => prev - 1);
            
            showSuccess('Đã xóa chia sẻ thành công!');
            
            setShowDeleteConfirm(null);
        } catch (err) {
            console.error('Error deleting share:', err);
            setError('Có lỗi xảy ra khi xóa chia sẻ');
            setShowDeleteConfirm(null);
        }
    };

    const copyToClipboard = async (url, shareId) => {
        try {
            await navigator.clipboard.writeText(url);
            
            showSuccess('Đã copy link chia sẻ!');
            
            // Hiệu ứng flash cho icon
            const button = document.getElementById(`copy-${shareId}`);
            if (button) {
                const originalClass = button.className;
                button.className = button.className.replace('text-blue-600', 'text-green-600');
                setTimeout(() => {
                    button.className = originalClass;
                }, 2000);
            }
        } catch (err) {
            console.error('Failed to copy:', err);
            showError('Lỗi khi copy link!');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('vi-VN');
    };

    const getFullUrl = (shareUrl) => {
        return `${window.location.origin}${shareUrl}`;
    };

    if (loading && shares.length === 0) {
        return (
            <div className="px-6 py-3">
                <div className="animate-pulse space-y-2">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="flex items-center py-1.5">
                            {/* Title skeleton */}
                            <div className="flex-1 min-w-0 pr-3">
                                <div className="h-4 bg-app-hover rounded w-3/4"></div>
                            </div>
                            {/* Share Type skeleton */}
                            <div className="w-20 px-1">
                                <div className="h-5 bg-app-hover rounded w-16"></div>
                            </div>
                            {/* Date skeleton */}
                            <div className="w-28 px-1">
                                <div className="h-3 bg-app-hover rounded w-20"></div>
                            </div>
                            {/* Actions skeleton */}
                            <div className="flex items-center gap-1 flex-shrink-0 w-14 justify-end">
                                <div className="w-4 h-4 bg-app-hover rounded"></div>
                                <div className="w-4 h-4 bg-app-hover rounded"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="max-h-[420px] overflow-y-auto" ref={scrollContainerRef}>
            <style>{`
                @keyframes slideInRight {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                
                /* Custom tooltip styles */
                .tooltip-btn {
                    position: relative;
                }
                
                .tooltip-btn:hover::after {
                    content: attr(data-tooltip);
                    position: absolute;
                    bottom: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    background: rgba(0, 0, 0, 0.9);
                    color: white;
                    padding: 8px 12px;
                    border-radius: 6px;
                    font-size: 14px;
                    white-space: nowrap;
                    z-index: 9999;
                    margin-bottom: 5px;
                    pointer-events: none;
                }
                
                .tooltip-btn:hover::before {
                    content: '';
                    position: absolute;
                    bottom: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    border: 6px solid transparent;
                    border-top-color: rgba(0, 0, 0, 0.9);
                    z-index: 9999;
                    margin-bottom: -1px;
                    pointer-events: none;
                }
            `}</style>
            <div className="px-6 py-3">
            <ErrorMessage message={error} className="mb-6" />

            {shares.length === 0 ? (
                <div className="text-center py-12">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có chia sẻ nào</h3>
                    <p className="text-gray-500">Bạn chưa chia sẻ cuộc trò chuyện hoặc tin nhắn nào.</p>
                </div>
            ) : (
                <div className="space-y-0">
                    {shares.map((share) => (
                        <div key={share.share_id} className="flex items-center py-1.5 border-b border-gray-100">
                            {/* Column 1: Title */}
                            <div className="flex-1 min-w-0 max-w-[400px] pr-3">
                                <a
                                    href={getFullUrl(share.share_url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-gray-900 hover:text-brand-blue-600 underline truncate block"
                                >
                                    {share.title || 'Untitled Share'}
                                </a>
                            </div>

                            {/* Column 2: Share Type */}
                            <div className="w-24 px-1 flex justify-center">
                                <span className="px-2 py-0.5 rounded text-xs bg-[#00000015] text-gray-800 block text-center w-full">
                                    {share.share_type === 'session' ? 'Phiên chat' : 'Tin nhắn'}
                                </span>
                            </div>

                            {/* Column 3: Date */}
                            <div className="w-28 px-1">
                                <span className="text-xs text-gray-500">{formatDate(share.created_at)}</span>
                            </div>

                            {/* Column 4: Actions */}
                            <div className="flex items-center gap-1 flex-shrink-0 w-14 justify-end">
                                {/* Copy Button */}
                                <button
                                    id={`copy-${share.share_id}`}
                                    onClick={() => copyToClipboard(getFullUrl(share.share_url), share.share_id)}
                                    className="p-1 text-gray-600 hover:text-brand-blue-600 transition-colors"
                                    title="Copy link"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </button>

                                {/* Delete Button */}
                                <button
                                    onClick={() => setShowDeleteConfirm(share.share_id)}
                                    className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                                    title="Xóa"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}

                    {/* Loading More Indicator */}
                    {loadingMore && (
                        <div className="flex justify-center py-4">
                            <div className="flex items-center gap-2 text-gray-500 text-sm">
                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                Đang tải thêm...
                            </div>
                        </div>
                    )}

                    {/* End of list indicator */}
                    {page >= totalPages && shares.length > 0 && (
                        <div className="text-center py-4 text-sm text-gray-400">
                            Đã hiển thị tất cả
                        </div>
                    )}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Xác nhận xóa</h3>
                        <p className="text-gray-600 mb-6">
                            Bạn có chắc chắn muốn xóa chia sẻ này? Hành động này không thể hoàn tác.
                        </p>
                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className="flex-1 px-4 py-2 border border-app-border text-gray-700 rounded-md hover:bg-app-hover"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={() => handleDelete(showDeleteConfirm)}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                                Xóa
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default ShareManagement;
